import type { AliasDto } from "@bafe/api-client";
import { ArrowBendUpRightIcon, PlusIcon } from "@phosphor-icons/react";
import {
	Button,
	Card,
	EmptyState,
	Field,
	InlineNotice,
	Input,
	PageHeader,
	Section,
	Select,
	SkeletonRows,
	StatusPill,
	useToast,
} from "@bafe/ui";
import { validateLocalPart } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ConfirmDialog } from "../components/ConfirmDialog";
import { ErrorPanel } from "../components/ErrorPanel";
import { LinkButton } from "../components/LinkButton";
import { api } from "../lib/api";
import { isMailboxAlias } from "../lib/mail-addresses";
import { queryKeys } from "../lib/query";
import { ALIAS_STATUS } from "../lib/status";

export function Aliases() {
	const [creating, setCreating] = useState(false);
	const [removing, setRemoving] = useState<AliasDto | null>(null);
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const aliases = useQuery({ queryKey: queryKeys.aliases, queryFn: () => api.listAliases() });
	const domains = useQuery({ queryKey: queryKeys.domains, queryFn: () => api.listDomains() });

	const remove = useMutation({
		mutationFn: (alias: AliasDto) => api.deleteAlias(alias.id),
		onSuccess: () => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.aliases });
			setRemoving(null);
			toast({ tone: "success", message: "Alias removed." });
		},
	});

	const rows = (aliases.data ?? []).filter(isMailboxAlias);
	const hasDomains = (domains.data ?? []).length > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Aliases"
				description="Extra addresses that deliver into an existing mailbox."
				action={
					hasDomains ? (
						<Button
							variant="primary"
							onClick={() => setCreating(true)}
							iconLeft={<PlusIcon className="size-4" aria-hidden="true" />}
						>
							Add alias
						</Button>
					) : undefined
				}
			/>

			<InlineNotice tone="info" title="An alias is not a mailbox">
				An alias has no password, no storage and nobody signs into it. Mail sent to it lands in the mailbox
				you point it at.
			</InlineNotice>

			{aliases.isPending && <SkeletonRows rows={5} />}

			{aliases.isError && (
				<ErrorPanel
					error={aliases.error}
					title="We could not load your aliases."
					onRetry={() => void aliases.refetch()}
					retryPending={aliases.isFetching}
				/>
			)}

			{aliases.isSuccess && (
				<Section>
					{rows.length === 0 ? (
						<Card>
							<EmptyState
								icon={<ArrowBendUpRightIcon className="size-8" aria-hidden="true" />}
								title="No aliases yet"
								description={
									hasDomains
										? "Add an address like sales@ or hello@ and point it at a mailbox you already have."
										: "Add a domain first, then you can point extra addresses at your mailboxes."
								}
								action={
									hasDomains ? (
										<Button variant="primary" onClick={() => setCreating(true)}>
											Add your first alias
										</Button>
									) : (
										<LinkButton to="/manage/domains/new" variant="primary">
											Add a domain
										</LinkButton>
									)
								}
							/>
						</Card>
					) : (
						<Card flush>
							<ul>
								{rows.map((alias) => (
									<li
										key={alias.id}
										className="flex flex-wrap items-center justify-between gap-3 border-b border-(--border-subtle) px-4 py-3.5 last:border-0"
									>
										<div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
											<span className="text-dns font-medium text-(--text-primary)">
												{alias.sourceAddress}
											</span>
											<span aria-hidden="true" className="text-(--text-muted)">
												→
											</span>
											<span className="sr-only">forwards to</span>
											<span className="text-dns text-(--text-secondary)">
												{alias.destinationLabel || alias.destinationValue}
											</span>
										</div>

										<div className="flex items-center gap-3">
											<StatusPill tone={ALIAS_STATUS[alias.status].tone}>
												{ALIAS_STATUS[alias.status].label}
											</StatusPill>
											<Button size="sm" variant="ghost" onClick={() => setRemoving(alias)}>
												Remove<span className="sr-only"> {alias.sourceAddress}</span>
											</Button>
										</div>
									</li>
								))}
							</ul>
						</Card>
					)}
				</Section>
			)}

			{creating && <CreateAliasDialog onClose={() => setCreating(false)} />}

			<ConfirmDialog
				open={removing !== null}
				onClose={() => setRemoving(null)}
				title="Remove this alias?"
				description={
					removing
						? `Mail sent to ${removing.sourceAddress} will stop arriving. Messages already delivered stay where they are.`
						: undefined
				}
				confirmLabel="Remove alias"
				danger
				pending={remove.isPending}
				error={remove.error}
				onConfirm={() => {
					if (removing) remove.mutate(removing);
				}}
			/>
		</div>
	);
}

function CreateAliasDialog({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const { toast } = useToast();

	const domains = useQuery({ queryKey: queryKeys.domains, queryFn: () => api.listDomains() });
	const mailboxes = useQuery({
		queryKey: queryKeys.mailboxes(),
		queryFn: () => api.listMailboxes({ limit: 200 }),
	});

	const domainList = domains.data ?? [];
	const mailboxList = mailboxes.data?.data ?? [];

	const [domainId, setDomainId] = useState(() => domainList[0]?.id ?? "");
	const [localPart, setLocalPart] = useState("");
	const [mailboxId, setMailboxId] = useState(() => mailboxList[0]?.id ?? "");

	const effectiveDomainId = domainId || (domainList[0]?.id ?? "");
	const effectiveMailboxId = mailboxId || (mailboxList[0]?.id ?? "");
	const domain = domainList.find((item) => item.id === effectiveDomainId) ?? null;
	const result = validateLocalPart(localPart);

	const create = useMutation({
		mutationFn: () =>
			api.createAlias({
				domainId: effectiveDomainId,
				localPart: result.normalized,
				destinationType: "mailbox",
				mailboxId: effectiveMailboxId,
				externalAddress: null,
			}),
		onSuccess: (alias) => {
			void queryClient.invalidateQueries({ queryKey: queryKeys.aliases });
			toast({ tone: "success", message: `${alias.sourceAddress} is set up.` });
			onClose();
		},
	});

	const ready = result.ok && effectiveDomainId !== "" && effectiveMailboxId !== "";

	function submit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!ready) return;
		create.mutate();
	}

	return (
		<ConfirmDialog
			open
			onClose={onClose}
			title="Add an alias"
			description="Choose the new address and the mailbox that should receive its mail."
			confirmLabel="Add alias"
			pending={create.isPending}
			error={create.error}
			onConfirm={() => {
				if (ready) create.mutate();
			}}
		>
			<form className="flex flex-col gap-4" onSubmit={submit} noValidate>
				<div className="grid gap-3 sm:grid-cols-2">
					<Field
						label="Alias address"
						error={localPart.length > 0 && !result.ok ? (result.message ?? null) : null}
						required
					>
						{({ id, describedBy, invalid }) => (
							<Input
								id={id}
								value={localPart}
								required
								autoFocus
								autoComplete="off"
								autoCapitalize="none"
								spellCheck={false}
								placeholder="sales"
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={(event) => setLocalPart(event.target.value)}
							/>
						)}
					</Field>

					<Field label="Domain" required>
						{({ id }) => (
							<Select
								id={id}
								value={effectiveDomainId}
								onChange={(event) => setDomainId(event.target.value)}
							>
								{domainList.map((item) => (
									<option key={item.id} value={item.id}>
										{item.domainName}
									</option>
								))}
							</Select>
						)}
					</Field>
				</div>

				{result.ok && domain && (
					<p className="text-sm text-(--text-secondary)">
						<span className="text-dns text-(--text-primary)">
							{result.normalized}@{domain.domainName}
						</span>{" "}
						will forward to the destination below.
					</p>
				)}

				<Field label="Mailbox" required>
					{({ id }) => (
						<Select
							id={id}
							value={effectiveMailboxId}
							onChange={(event) => setMailboxId(event.target.value)}
						>
							{mailboxList.map((item) => (
								<option key={item.id} value={item.id}>
									{item.emailAddress}
								</option>
							))}
						</Select>
					)}
				</Field>
			</form>
		</ConfirmDialog>
	);
}
